import React, { useContext, useEffect, useState } from "react";
import { Grid, Loader, Message } from "rsuite";
import { PropertyStoreContext } from "../../../contexts/PropertyStoreContext";
import { getPropertyMetadata, getCRS } from "../../../api";
import BuildingIcon from "../../../assets/img/property-config/building.svg?react";
import MapPinIcon from "../../../assets/img/property-config/map-pin.png";
import ClockIcon from "../../../assets/img/property-config/clock.svg?react";
import PeopleIcon from "../../../assets/img/property-config/people.svg?react";
import StarIcon from "../../../assets/img/star.svg?react";
import PropertyMap from "../../../components/property-map/PropertyMap";
import PropertyConfigHeader from "../PropertyConfigHeader";
import { getCrsDisplayName, getChildAgeRanges } from "./utils";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import "../propertyConfig.scss";
import "./information.scss";

const FieldGroup = ({ label, value, className = "" }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className={`info-field ${className}`}>
      <div className="info-field__label">{label}</div>
      <div className="info-field__value">{value}</div>
    </div>
  );
};

const StarRating = ({ rating }) => {
  const numRating = Math.min(Math.max(Number.parseFloat(rating) || 0, 0), 5);
  const fullStars = Math.floor(numRating);
  const fraction = numRating % 1;

  return (
    <div className="star-rating">
      {new Array(5).fill().map((num, index) => {
        if (index < fullStars) {
          return <StarIcon key={`${index}${num}`} className="star-filled" />;
        }
        if (index === fullStars && fraction > 0) {
          return (
            <span
              key={`${index}${num}`}
              className="star-partial-wrapper"
              style={{ "--star-fill": `${Math.round(fraction * 100)}%` }}
            >
              <StarIcon className="star-empty" />
              <StarIcon className="star-filled star-partial-overlay" />
            </span>
          );
        }
        return <StarIcon key={`${index}${num}`} className="star-empty" />;
      })}
    </div>
  );
};

const SectionCard = ({ icon: Icon, iconSrc: IconSrc, title, children }) => (
  <div className="info-section-card">
    <div className="info-section-card__header">
      <div className="info-section-card__title-wrapper">
        {Icon && <Icon className="info-section-card__icon" />}
        {IconSrc && (
          <img src={IconSrc} alt="" className="info-section-card__icon" />
        )}
        <h3 className="info-section-card__title">{title}</h3>
      </div>
    </div>
    <div className="info-section-card__content">{children}</div>
  </div>
);

const Information = () => {
  const { selectedProperty } = useContext(PropertyStoreContext);
  const [metadata, setMetadata] = useState(null);
  const [crs, setCrs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      if (!selectedProperty?.propertyId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await Promise.all([
          getPropertyMetadata(selectedProperty.propertyId),
          getCRS(),
        ]);
        const [propertyMetadata, crsMetadata] = response;
        setMetadata(propertyMetadata.body.property);
        setCrs(crsMetadata.body);
      } catch (err) {
        setError(err.message || "Failed to load property metadata");
        getLogger().error("PropertyInfo", {
          type: "Error",
          page_url: window.location.href,
          error_desc: "Error fetching property metadata",
          error_stack: err?.stack ?? null,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, [selectedProperty?.propertyId]);

  return (
    <Grid fluid className="property-config-page">
      <PropertyConfigHeader title="Information" />

      <div className="property-config-page__content">
        {!selectedProperty && (
          <Message type="info">
            Please select a property to view information
          </Message>
        )}

        {loading && (
          <div className="property-config-page__loader">
            <Loader size="md" content="Loading property metadata..." />
          </div>
        )}

        {error && (
          <Message type="error" showIcon>
            {error}
          </Message>
        )}

        {!loading && !error && metadata && (
          <>
            {/* Details Section */}
            <SectionCard icon={BuildingIcon} title="Details">
              <div className="info-row">
                <FieldGroup
                  label="Property Code"
                  value={metadata.code || selectedProperty?.propertyCode}
                />
                <FieldGroup
                  label="Chain"
                  value={metadata.chainName || "Independent"}
                />
                <FieldGroup
                  label="Brand"
                  value={metadata.brandName || "Independent"}
                />
                <FieldGroup
                  label="Property Name"
                  value={metadata.name || selectedProperty?.propertyName}
                />
              </div>
              <div className="info-row">
                <FieldGroup
                  label="Management Company"
                  value={metadata.managementCompany || "Independent"}
                />
                <FieldGroup
                  label="CRS"
                  value={getCrsDisplayName(crs, metadata.crsId)}
                />
                <div className="info-field">
                  <div className="info-field__label">Star Rating</div>
                  <StarRating rating={metadata.starRating || 0} />
                </div>
                <div className="info-field"></div>
              </div>
              {metadata.checkInPolicy && (
                <div className="info-row info-row--single">
                  <FieldGroup
                    label="Check-in Policy"
                    value={metadata.checkInPolicy}
                    className="info-field--full-width"
                  />
                </div>
              )}
              {metadata.shortDescription && (
                <div className="info-row info-row--single">
                  <FieldGroup
                    label="Short Description"
                    value={metadata.shortDescription}
                    className="info-field--full-width"
                  />
                </div>
              )}
              {metadata.longDescription && (
                <div className="info-row info-row--single">
                  <FieldGroup
                    label="Long Description"
                    value={metadata.longDescription}
                    className="info-field--full-width"
                  />
                </div>
              )}
            </SectionCard>

            {/* Address & Contact Section */}
            <SectionCard iconSrc={MapPinIcon} title="Address & Contact Details">
              <div className="info-section-grid">
                <div className="info-section-grid__map">
                  <PropertyMap
                    latitude={metadata.latitude}
                    longitude={metadata.longitude}
                  />
                </div>
                <div className="info-section-grid__details">
                  <div className="info-row info-row--single">
                    <FieldGroup
                      label="Address"
                      value={`${metadata.address1 || ""}${metadata.address2 ? ", " + metadata.address2 : ""}, ${metadata.city || ""}, ${metadata.state || ""} ${metadata.zip || ""}, ${metadata.country || ""}`}
                      className="info-field--full-width"
                    />
                  </div>
                  <div className="info-row info-row--three">
                    <FieldGroup label="Latitude" value={metadata.latitude} />
                    <FieldGroup label="Longitude" value={metadata.longitude} />
                    <FieldGroup label="Time Zone" value={metadata.timezoneId} />
                  </div>
                  <div className="info-row info-row--three">
                    <FieldGroup label="Phone number" value={metadata.phone1} />
                    <FieldGroup
                      label="Primary Email"
                      value={metadata.primaryEmail}
                    />
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Check-In Policies Section */}
            <SectionCard icon={ClockIcon} title="Check-In Policies">
              <div className="info-row info-row--three">
                <FieldGroup
                  label="Check-in Time"
                  value={`${String(metadata.checkInHours || 0).padStart(2, "0")}:${String(metadata.checkInMinutes || 0).padStart(2, "0")}`}
                />
                <FieldGroup
                  label="Check-Out Time"
                  value={`${String(metadata.checkOutHours || 0).padStart(2, "0")}:${String(metadata.checkOutMinutes || 0).padStart(2, "0")}`}
                />
                <FieldGroup
                  label="Min Adult Age at Check-in"
                  value={metadata.minAdultAgeCheckIn}
                />
              </div>
            </SectionCard>

            {/* Age Restrictions Section */}
            <SectionCard icon={PeopleIcon} title="Age Restrictions">
              <div className="info-row">
                {(() => {
                  const { infants, child } = getChildAgeRanges(
                    metadata.childAgeRanges
                  );
                  return (
                    <>
                      <FieldGroup label="Max Child Age" value={child?.maxAge} />
                      <FieldGroup label="Min Child Age" value={child?.minAge} />
                      <FieldGroup
                        label="Min Free Infant Age"
                        value={infants?.minAge}
                      />
                      <FieldGroup
                        label="Max Free Infant Age"
                        value={infants?.maxAge}
                      />
                    </>
                  );
                })()}
              </div>
            </SectionCard>
          </>
        )}

        {!loading && !error && !metadata && selectedProperty && (
          <Message type="info">No metadata available for this property</Message>
        )}
      </div>
    </Grid>
  );
};

export default Information;
